/**
 * UAVTradeZone Modification Record
 *
 * Change ID: REFACTOR_MOBILE_BROADCAST_GRID
 * Date: 2026-02-10
 * Modified By: Claude
 *
 * Description:
 * Refactored MobileBroadcastList.tsx to use a single CSS Grid container with two rows,
 * instead of two nested grid containers, for improved layout control and semantic structure.
 *
 * File Size Check:
 * - Current: 129 lines
 * - Adding: ~10 lines
 * - Projected: 139 lines
 * - Decision: Proceed (within component limit)
 *
 * Related Files:
 * - client/src/features/dashboard/components/MobileBroadcastList.tsx
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { BroadcastItem, UserInfo } from "../../../interfaces/broadcast";
import {
  formatRelativeTimeCompact,
} from "../utils/broadcastHelpers"; // [2026-02-10] compact-time: use short format for mobile
// [2026-02-13] CURRENCY_PHASE2: Centralized single-price display
import { formatCurrency } from "../../../utils/priceDisplay";

// Match the FlatItem type from DashboardBroadcastSummaryTable
interface FlatItem extends BroadcastItem {
  broadcastType: "WTB" | "WTS";
  broadcastId: string;
  createdAt: string;
  userId?: string | UserInfo;
  status?: "draft" | "sent" | "archived" | "deleted";
  sentAt?: string | null;
}

interface MobileBroadcastListProps {
  items: FlatItem[];
  showTypeColumn?: boolean;
  showFooter?: boolean;       // [2026-02-10] Show Row 3 (company + optional actions)
  showActions?: boolean;      // [2026-02-10] Show Edit/Delete buttons in footer
  onItemClick?: (item: FlatItem) => void;
  onEdit?: (item: FlatItem) => void;    // [2026-02-10] Edit handler
  onDelete?: (item: FlatItem) => void;  // [2026-02-10] Delete handler
}

// [2026-02-10] Helper to extract company name from userId
const getCompanyName = (userId: string | UserInfo | undefined): string => {
  if (!userId || typeof userId === "string") return "—";
  return userId.companyName || `${userId.firstName || ""} ${userId.lastName || ""}`.trim() || "—";
};

export const MobileBroadcastList: React.FC<MobileBroadcastListProps> = ({
  items,
  showTypeColumn = true,
  showFooter = false,
  showActions = false,
  onItemClick,
  onEdit,
  onDelete,
}) => {
  const navigate = useNavigate();

  const handleCardClick = (item: FlatItem) => {
    if (onItemClick) {
      onItemClick(item);
    } else {
      navigate(`/allbroadcasts/item/${item.broadcastId}/${item.itemNo}`);
    }
  };

  if (!items.length) {
    return (
      <div className="text-center py-4">
        <div className="fw-semibold mb-1">No broadcasts found</div>
        <div className="text-muted">Create a broadcast to see it here.</div>
      </div>
    );
  }

  return (
    <div className="mobile-list-container">
      {items.map((item, idx) => (
        <div
          key={`${item.broadcastId}-${idx}`}
          className="broadcast-mobile-card touch-target"
          onClick={() => handleCardClick(item)}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => e.key === "Enter" && handleCardClick(item)}
        >
          <div className={`broadcast-mobile-card__grid${showFooter ? " broadcast-mobile-card__grid--with-footer" : ""}`}>
            {/* Row 1: Header */}
            <div className="mobile-list-card__header broadcast-mobile-card__header-grid">
              <div className="broadcast-mobile-card__info">
                <p className="broadcast-mobile-card__manufacturer">
                  {item.manufacturer || "—"}
                </p>
                <p className="broadcast-mobile-card__part">
                  {item.partNumber || "—"}
                </p>
              </div>
              {showTypeColumn && (
                <div className="broadcast-mobile-card__type-cell">
                  <span
                    className={`broadcast-mobile-card__type ${item.broadcastType.toLowerCase()}`}
                  >
                    {item.broadcastType}
                  </span>
                </div>
              )}
            </div>

            {/* Row 2: Details */}
            <div className="broadcast-mobile-card__details broadcast-mobile-card__details-grid">
              <span
                className={`broadcast-mobile-card__condition-badge broadcast-mobile-card__condition-badge--${(item.condition || '').toLowerCase().replace(/\s+/g, '-')}`}
              >
                {item.condition || "—"}
              </span>
              <span className="broadcast-mobile-card__detail-value broadcast-mobile-card__detail-qty">
                Qty: {item.quantity ?? "—"}
              </span>
              <span className="broadcast-mobile-card__detail-value broadcast-mobile-card__detail-value--price">
                {formatCurrency(item.price, item.currency, (item as any).convertedPrice, (item as any).displayCurrency) || "N/A"}
              </span>
              <span className="broadcast-mobile-card__detail-value broadcast-mobile-card__detail-value--time">
                {formatRelativeTimeCompact(item.createdAt)}
              </span>
            </div>

            {/* [2026-02-10] Row 3: Footer (Variant 2) */}
            {showFooter && (
              <div className="broadcast-mobile-card__footer">
                <span className="broadcast-mobile-card__company">
                  {getCompanyName(item.userId)}
                </span>
                {showActions && onEdit && onDelete && (
                  <div className="broadcast-mobile-card__actions">
                    <button
                      className="broadcast-mobile-card__action-btn broadcast-mobile-card__action-btn--edit"
                      onClick={(e) => { e.stopPropagation(); onEdit(item); }}
                    >
                      Edit
                    </button>
                    <button
                      className="broadcast-mobile-card__action-btn broadcast-mobile-card__action-btn--delete"
                      onClick={(e) => { e.stopPropagation(); onDelete(item); }}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default MobileBroadcastList;
